import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';

import { HeaderNavTabs } from '@/components/HeaderNavTabs';
import { LIVE_BADGE_HEIGHT, LIVE_LABEL_WITH_BADGE_WIDTH, LiveBadge } from '@/components/LiveBadge';
import { Section } from '@/components/Section';
import { NEW_TOP_TAB_BAR_CONFIG, NEW_TOP_TAB_BAR_HEIGHT } from '@/components/TopTabBar';
import { filteredEntriesKeys } from '@/feature/entries-pickem/hooks/use-infinite-filtered-entries';
import { useLiveEntriesCount } from '@/feature/entries-pickem/hooks/use-live-entries-count';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useResumeEffect } from '@/hooks/use-resume';
import { EventStatus } from '@/types/api.generated';
import { useQueryClient } from '@tanstack/react-query';

import { EntriesList } from '../components/EntriesList';

export interface Section {
    label: 'Upcoming' | 'Live' | 'Finished';
    eventStatus?: EventStatus;
}

type SECTION_KEYS = 'UPCOMING' | 'LIVE' | 'FINISHED';
type SECTION_SCREENS = `Entries-${SECTION_KEYS}`;
type Sections = {
    [K in SECTION_KEYS]: Section;
};

export type EntriesTabParamsList = {
    [K in SECTION_SCREENS]: {
        currentSection: Section;
    };
};

const SECTIONS: Sections = {
    UPCOMING: { label: 'Upcoming', eventStatus: EventStatus.Scheduled },
    LIVE: { label: 'Live', eventStatus: EventStatus.InProgress },
    FINISHED: { label: 'Finished', eventStatus: EventStatus.Final },
};

const SECTIONS_ORDER: SECTION_KEYS[] = ['UPCOMING', 'LIVE', 'FINISHED'];

// known warning from react-native 0.72+ regarding `onAnimatedValueUpdate` with no exact fix for tabs
// https://github.com/react-navigation/react-navigation/issues/7839
const Tab = createMaterialTopTabNavigator<EntriesTabParamsList>();

export const EntriesTopTabBarScreen = () => {
    const { t } = useTranslation(['pickem_entries']);
    const liveCount = useLiveEntriesCount();
    const queryClient = useQueryClient();

    // Refetch InProgress so the badge reflects entries that became live while away.
    useResumeEffect(
        useCallback(() => {
            queryClient.refetchQueries({ queryKey: filteredEntriesKeys.byStatus(EventStatus.InProgress) });
        }, [queryClient])
    );

    return (
        <MaxWidthWrapper flex={1}>
            <HeaderNavTabs<EntriesTabParamsList> title={t('my_entries')} initialRouteName="Entries-UPCOMING">
                {SECTIONS_ORDER.map(sectionKey => {
                    const liveTab = sectionKey === 'LIVE';
                    const showLiveBadge = liveTab && liveCount > 0;
                    return (
                        <Tab.Screen
                            name={`Entries-${sectionKey}`}
                            key={sectionKey}
                            options={{
                                title: SECTIONS[sectionKey].label,
                                tabBarBadge: showLiveBadge ? () => renderLiveBadge(liveCount) : undefined,
                                tabBarLabelStyle: showLiveBadge
                                    ? [NEW_TOP_TAB_BAR_CONFIG.tabBarLabelStyle, styles.liveTabLabelWithBadge]
                                    : NEW_TOP_TAB_BAR_CONFIG.tabBarLabelStyle,
                            }}
                            initialParams={{ currentSection: SECTIONS[sectionKey] }}
                            component={EntriesList}
                        />
                    );
                })}
            </HeaderNavTabs>
        </MaxWidthWrapper>
    );
};

const renderLiveBadge = (liveCount: number) => <LiveBadge count={liveCount} style={styles.badge} />;

const styles = StyleSheet.create({
    liveTabLabelWithBadge: {
        width: LIVE_LABEL_WITH_BADGE_WIDTH,
    },
    badge: {
        height: LIVE_BADGE_HEIGHT,
        top: (NEW_TOP_TAB_BAR_HEIGHT - LIVE_BADGE_HEIGHT) / 2,
    },
});
